const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const buildSuperFanAction = (label = 'Open Super Fan Accounts', tab = 'superfanaccounts', roomId = 'super-fans') => ({
  label,
  tab,
  roomId,
});

export function buildSuperFansRoomContext({
  room,
  superFanOverview = null,
  primaryAction,
  secondaryAction,
}) {
  const stats = superFanOverview?.stats || {};
  const accounts = Array.isArray(superFanOverview?.partners) ? superFanOverview.partners : [];
  const inactiveCount = Number(stats.inactive_accounts || 0);
  const totalAccounts = Number(stats.total_accounts || 0);
  const layeredCount = Number(stats.additional_role_accounts || 0);
  const primaryTierCount = Number(stats.primary_tier_accounts || 0);
  const firstInactiveAccount = accounts.find((account) => String(account?.status || '').toLowerCase() !== 'active');

  const priorityItems = [];

  if (firstInactiveAccount) {
    priorityItems.push({
      tone: 'attention',
      eyebrow: 'Review first',
      title: `${firstInactiveAccount.name || firstInactiveAccount.email || 'A super fan'} may no longer be active`,
      detail: 'Super Fan access should stay tight and intentional. Confirm whether this person still belongs here.',
      location: 'In Super Fan Accounts',
    });
  }

  if (layeredCount > 0) {
    priorityItems.push({
      tone: priorityItems.length ? 'next' : 'attention',
      eyebrow: priorityItems.length ? 'Then' : 'Do first',
      title: `${pluralize(layeredCount, 'account')} still carry Super Fan access as an additional role`,
      detail: 'Layered access can be right, but the list should stay explicitly chosen rather than drifting.',
      location: 'In Super Fan Accounts',
    });
  }

  if (!priorityItems.length) {
    priorityItems.push({
      tone: 'steady',
      eyebrow: 'Steady',
      title: 'The current Super Fan list looks quiet and intentional',
      detail: 'No access drift is surfacing right now, so keep this lane lightweight and focused on fit.',
      location: 'In Super Fan Accounts',
    });
  }

  return {
    id: room.id,
    variant: 'super-fans',
    statusTone: inactiveCount > 0 ? 'attention' : totalAccounts > 0 || layeredCount > 0 ? 'next' : 'steady',
    statusLabel: inactiveCount > 0 ? 'Needs review' : totalAccounts > 0 ? 'In motion' : 'Ready',
    startTitle: inactiveCount > 0
      ? 'Start by confirming who should still carry Super Fan access'
      : 'Keep the Super Fan list intentionally small and clean',
    startDetail: 'This room is intentionally shallow. It should help you see who has Super Fan access, how that access is granted, and whether the current list still reflects the right people.',
    signals: [
      {
        tone: totalAccounts > 0 ? 'next' : 'steady',
        text: totalAccounts > 0
          ? `${pluralize(totalAccounts, 'super fan account')} currently have access`
          : 'No super fan accounts are currently surfaced.',
      },
      {
        tone: layeredCount > 0 ? 'next' : 'steady',
        text: layeredCount > 0
          ? `${pluralize(layeredCount, 'account')} carry super-fan access as an additional role`
          : 'No super-fan access is currently layered through additional roles.',
      },
      {
        tone: inactiveCount > 0 ? 'attention' : 'steady',
        text: inactiveCount > 0
          ? `${pluralize(inactiveCount, 'account')} may no longer be active`
          : 'No inactive super-fan accounts are currently surfaced.',
      },
    ],
    metrics: [
      {
        label: 'Total accounts',
        value: totalAccounts,
        detail: 'People who currently carry super-fan access',
        tone: totalAccounts > 0 ? 'next' : 'steady',
      },
      {
        label: 'Primary tier',
        value: primaryTierCount,
        detail: 'Accounts where super_fan is the primary tier',
        tone: primaryTierCount > 0 ? 'next' : 'steady',
      },
      {
        label: 'Layered access',
        value: layeredCount,
        detail: 'Accounts where super_fan is granted as an additional role',
        tone: layeredCount > 0 ? 'next' : 'steady',
      },
    ],
    priorityItems,
    watchListTitle: 'Super Fan watchlist',
    watchListItems: [
      inactiveCount > 0
        ? {
            title: `${pluralize(inactiveCount, 'account')} need access review`,
            detail: 'Confirm whether these people should still be in the Super Fan circle.',
            tone: 'attention',
          }
        : null,
      layeredCount > 0
        ? {
            title: `${pluralize(layeredCount, 'account')} are still layered instead of primary`,
            detail: 'Layered access should stay a deliberate choice, not a leftover state.',
            tone: 'next',
          }
        : null,
    ].filter(Boolean),
    primaryAction: primaryAction || buildSuperFanAction(),
    secondaryAction: secondaryAction || null,
  };
}
